<template>
  <div
    id="app"
    :class="[shouldShowNavbar ? 'app-with-nav' : 'app-without-nav', uiStore.sidebarCollapsed && 'sidebar-collapsed']"
  >
    <Sidebar v-if="shouldShowNavbar" />

    <div class="app-main">
      <header v-if="shouldShowNavbar" class="app-header">
        <div :class="['h-1', authStore.currentContext === 'employee' ? 'bg-purple-600' : 'bg-green-600']"></div>

        <div class="flex items-center justify-between px-6 py-3">
          <div class="flex items-center gap-4">
            <div class="block lg:hidden">
              <Button
                icon="pi pi-bars"
                text
                rounded
                aria-label="Toggle mobile menu"
                @click="uiStore.toggleMobileMenu()"
              />
            </div>
            <div class="hidden lg:block">
              <Button
                :icon="uiStore.sidebarCollapsed ? 'pi pi-angle-right' : 'pi pi-angle-left'"
                text
                rounded
                aria-label="Toggle sidebar"
                @click="uiStore.toggleSidebar()"
              />
            </div>

            <div>
              <div class="text-lg font-semibold text-gray-800 dark:text-gray-100">
                {{ greetingMessage }}
              </div>
              <div class="text-sm text-gray-500 dark:text-gray-400">
                {{ formattedDate }}
              </div>
            </div>
          </div>

          <div class="flex-1"></div>

          <div class="flex items-center gap-4">
            <Button
              v-if="authStore.isEmployee && authStore.isCustomer"
              :icon="authStore.currentContext === 'employee' ? 'pi pi-user' : 'pi pi-shield'"
              :label="authStore.currentContext === 'employee' ? 'Switch to Customer' : 'Switch to Admin'"
              outlined
              size="small"
              :severity="authStore.currentContext === 'employee' ? 'secondary' : 'success'"
              @click="switchContext"
            />

            <GlobalSearch v-if="authStore.currentContext === 'employee'" />
            <CustomerGlobalSearch v-else-if="authStore.currentContext === 'customer'" />

            <div
              v-if="authStore.isEmployee || authStore.isCustomer"
              class="h-6 w-px bg-gray-300 dark:bg-gray-600"
            ></div>

            <div class="flex items-center gap-1">
              <Button
                v-tooltip.bottom="uiStore.soundEnabled ? 'Disable sound notifications' : 'Enable sound notifications'"
                :icon="uiStore.soundEnabled ? 'pi pi-volume-up' : 'pi pi-volume-off'"
                text
                rounded
                :severity="uiStore.soundEnabled ? 'secondary' : 'danger'"
                @click="uiStore.toggleSound()"
              />

              <ThemeToggle />
            </div>
          </div>
        </div>
      </header>

      <main class="app-content">
        <router-view :key="route.path" />
      </main>

      <footer v-if="shouldShowNavbar" class="app-footer">
        <div class="px-6 py-4">
          <div class="flex items-center justify-between">
            <div class="text-sm text-gray-600 dark:text-gray-400">
              © {{ currentYear !== 2025 ? `2025-${currentYear}` : currentYear }} Oxplay. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>

    <Toast position="top-right" />
    <ConfirmDialog />
    <DeleteConfirmDialog />
    <DynamicDialog />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import Button from 'primevue/button';
import ConfirmDialog from 'primevue/confirmdialog';
import DynamicDialog from 'primevue/dynamicdialog';
import Toast from 'primevue/toast';

import { useTitle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';

import DeleteConfirmDialog from './components/Common/DeleteConfirmDialog.vue';
import CustomerGlobalSearch from './components/CustomerGlobalSearch.vue';
import GlobalSearch from './components/GlobalSearch.vue';
import Sidebar from './components/Sidebar.vue';
import ThemeToggle from './components/ThemeToggle.vue';
import { destroyEcho, initEcho } from './composables/useEcho';
import { loadAllTranslations } from './i18n';
import { routeDefinitions } from './router/route-definitions';
import { useAuthStore } from './stores/auth';
import { useUIStore } from './stores/ui';
import { formatDateTimeWithSeconds } from './utils/date';

const route = useRoute();
const { t } = useI18n();
const authStore = useAuthStore();
const uiStore = useUIStore();

const currentTime = ref(Date.now());

const currentYear = computed(() => new Date().getFullYear());

const greetingMessage = computed(() => {
  const hour = new Date(currentTime.value).getHours();
  let greeting = 'Good Evening';

  if (hour < 12) {
    greeting = 'Good Morning';
  } else if (hour < 18) {
    greeting = 'Good Afternoon';
  }

  const userName = authStore.currentUser?.name.split(' ')[0] || '';
  return userName ? `${greeting}, ${userName}` : greeting;
});

const formattedDate = computed(() => {
  const date = new Date(currentTime.value);
  return formatDateTimeWithSeconds(date);
});

const getPageTitleFromRoute = (): string => {
  const routeName = route.name as string;
  if (!routeName) {
    return 'Oxplay';
  }

  const findRouteDefinition = (obj: Record<string, unknown>, targetName: string): Record<string, unknown> | null => {
    for (const key in obj) {
      const value = obj[key];
      if (typeof value === 'object' && value !== null) {
        const objValue = value as Record<string, unknown>;
        if (objValue.name === targetName) {
          return objValue;
        }
        const found = findRouteDefinition(objValue, targetName);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  const routeDef = findRouteDefinition(routeDefinitions, routeName);

  const menus = routeDef?.menus as Record<string, { label?: string }> | undefined;
  if (menus && 'main' in menus && menus.main.label) {
    return t(menus.main.label);
  }

  return routeName
    .split('.')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const pageTitle = computed(() => {
  const title = getPageTitleFromRoute();
  const contextPrefix = authStore.currentContext === 'employee' ? '[Admin] ' : '';
  return title === 'Oxplay' ? title : `${contextPrefix}${title} - Oxplay`;
});

useTitle(pageTitle);

const switchContext = () => {
  if (authStore.currentContext === 'employee') {
    authStore.setContext('customer');
    window.location.href = '/';
  } else {
    authStore.setContext('employee');
    window.location.href = '/admin/dashboard';
  }
};

const shouldShowNavbar = computed(() => {
  if (route.meta.hideNavbar) {
    return false;
  }

  if (route.meta.requiresGuest) {
    return false;
  }

  if (authStore.isCustomer && route.name === 'customer.pending-invitations') {
    return false;
  }

  if (route.meta.context === 'employee') {
    return authStore.isEmployee;
  }

  if (route.meta.context === 'customer') {
    return authStore.isCustomer;
  }

  return authStore.isAuthenticated;
});

let timeUpdateInterval: ReturnType<typeof setInterval> | null = null;

watch(
  () => authStore.isAuthenticated,
  authenticated => {
    if (authenticated) {
      initEcho();
    } else {
      destroyEcho();
    }
  },
  { immediate: true },
);

onMounted(async () => {
  try {
    await loadAllTranslations();
  } catch (error) {
    console.error('Failed to load translations:', error);
  }

  await authStore.initAuth();

  timeUpdateInterval = setInterval(() => {
    currentTime.value = Date.now();
  }, 1000);
});

onBeforeUnmount(() => {
  if (timeUpdateInterval !== null) {
    clearInterval(timeUpdateInterval);
  }
});
</script>

<style>
body {
  margin: 0;
  padding: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
}

#app {
  min-height: 100vh;
  height: 100vh;
  background-color: #f9fafb;
  overflow: hidden;
}

.dark-mode #app {
  background-color: #0a0a0a;
}

.app-with-nav {
  display: grid;
  grid-template-columns: 16rem 1fr;
  grid-template-rows: 100vh;
  transition: grid-template-columns 0.3s ease;
}

.app-with-nav.sidebar-collapsed {
  grid-template-columns: 5rem 1fr;
}

.app-without-nav {
  display: flex;
  flex-direction: column;
}

.app-main {
  display: grid;
  grid-template-rows: auto 1fr auto;
  height: 100vh;
  overflow: hidden;
}

.app-header {
  border-bottom: 1px solid #e5e7eb;
  background-color: white;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}

.dark-mode .app-header {
  border-bottom-color: #27272a;
  background-color: #18181b;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.5);
}

.app-content {
  overflow-y: auto;
  overflow-x: hidden;
  background-color: #f9fafb;
}

.dark-mode .app-content {
  background-color: #0a0a0a;
}

.app-footer {
  border-top: 1px solid #e5e7eb;
  background-color: #f3f4f6;
}

.dark-mode .app-footer {
  border-top-color: #27272a;
  background-color: #18181b;
}

@media (max-width: 1023px) {
  .app-with-nav {
    grid-template-columns: 1fr;
  }
}
</style>
